import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';
import type { Json, Tables } from '../../types/database.types';
import { PARAPHEUR_COPY } from './parapheurCopy';

export type SignatureCircuit = Tables<'signature_circuits'>;
export type SignatureStep = Tables<'signature_steps'>;

export interface CircuitWithSteps extends SignatureCircuit {
  steps: Array<SignatureStep & {
    collaborator: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'> | null;
  }>;
  initiator: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'> | null;
  dossier: Pick<Tables<'dossiers'>, 'id' | 'title' | 'number'> | null;
  file: Pick<Tables<'dossier_files'>, 'id' | 'filename' | 'mime' | 'source_ref' | 'storage_path' | 'signed_storage_path'> | null;
}

export interface CreateCircuitInput {
  file_id: string;
  title: string;
  signers: Array<{ collaborator_id: string; deadline_at?: string | null }>;
  kind?: 'visa' | 'validation';
}

const CIRCUIT_SELECT_RELATIONS = `
  *,
  steps:signature_steps(
    *,
    collaborator:collaborators(id, full_name, avatar_url)
  ),
  initiator:collaborators!signature_circuits_initiator_id_fkey(id, full_name, avatar_url),
  dossier:dossiers(id, title, number),
  file:dossier_files!signature_circuits_dossier_file_id_fkey(id, filename, mime, source_ref, storage_path, signed_storage_path)
`;

export async function createCircuit(input: CreateCircuitInput): Promise<string> {
  const { data, error } = await supabase.rpc('create_signature_circuit', {
    p_file_id: input.file_id,
    p_title: input.title,
    p_signers: input.signers,
    p_kind: input.kind ?? 'visa',
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function signStep(stepId: string): Promise<void> {
  const { error } = await supabase.rpc('sign_step', { p_step_id: stepId });
  if (error) throw new Error(error.message);
}

export async function reauthAndSignStep(stepId: string, password: string): Promise<void> {
  const email = useAuthStore.getState().session?.user.email;
  if (!email) throw new Error('Session expirée');
  if (!password.trim()) throw new Error(PARAPHEUR_COPY.passwordRequired);
  const { error: authErr } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (authErr) throw new Error('Mot de passe incorrect');
  await signStep(stepId);
}

export async function setParapheZones(circuitId: string, zones: unknown): Promise<void> {
  const { error } = await supabase.rpc('set_paraphe_zones', {
    p_circuit_id: circuitId,
    p_zones: zones as Json,
  });
  if (error) throw new Error(error.message);
}

export async function setParapheStamps(stepId: string, zoneIds: string[]): Promise<void> {
  const { error } = await supabase.rpc('set_paraphe_stamps', {
    p_step_id: stepId,
    p_zone_ids: zoneIds,
  });
  if (error) throw new Error(error.message);
}

export async function rejectStep(
  stepId: string,
  comment: string,
  annotations: unknown = [],
): Promise<void> {
  const { error } = await supabase.rpc('reject_step', {
    p_step_id: stepId,
    p_comment: comment,
    p_annotations: annotations,
  } as never);
  if (error) throw new Error(error.message);
}

export async function cancelCircuit(circuitId: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_circuit', { p_circuit_id: circuitId });
  if (error) throw new Error(error.message);
}

export async function dispatchCircuit(circuitId: string): Promise<void> {
  const { error } = await supabase.rpc(
    'dispatch_signature_circuit' as never,
    { p_circuit_id: circuitId } as never,
  );
  if (error) throw new Error(error.message);
}

export async function resubmitCircuit(circuitId: string): Promise<number> {
  const { data, error } = await supabase.rpc(
    'resubmit_circuit_version' as never,
    { p_circuit_id: circuitId } as never,
  );
  if (error) throw new Error(error.message);
  return typeof data === 'number' ? data : Number(data);
}

export async function getCircuit(id: string): Promise<CircuitWithSteps | null> {
  const { data, error } = await supabase
    .from('signature_circuits')
    .select(CIRCUIT_SELECT_RELATIONS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as unknown as CircuitWithSteps) ?? null;
}

// Onglet « À signer » : mes steps pending
export async function listMyPendingSteps(): Promise<Array<SignatureStep & { circuit: CircuitWithSteps | null }>> {
  const collab = useAuthStore.getState().collaborator;
  if (!collab) return [];
  const { data, error } = await supabase
    .from('signature_steps')
    .select(`*, circuit:signature_circuits(${CIRCUIT_SELECT_RELATIONS})`)
    .eq('collaborator_id', collab.id)
    .eq('status', 'pending')
    .order('deadline_at', { ascending: true, nullsFirst: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Array<SignatureStep & { circuit: CircuitWithSteps | null }>;
}

export async function listMySignedSteps(): Promise<Array<SignatureStep & { circuit: CircuitWithSteps | null }>> {
  const collab = useAuthStore.getState().collaborator;
  if (!collab) return [];
  const { data, error } = await supabase
    .from('signature_steps')
    .select(`*, circuit:signature_circuits(${CIRCUIT_SELECT_RELATIONS})`)
    .eq('collaborator_id', collab.id)
    .eq('status', 'signed')
    .order('signed_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Array<SignatureStep & { circuit: CircuitWithSteps | null }>;
}

export async function listMyRejectedSteps(): Promise<Array<SignatureStep & { circuit: CircuitWithSteps | null }>> {
  const collab = useAuthStore.getState().collaborator;
  if (!collab) return [];
  const { data, error } = await supabase
    .from('signature_steps')
    .select(`*, circuit:signature_circuits(${CIRCUIT_SELECT_RELATIONS})`)
    .eq('collaborator_id', collab.id)
    .eq('status', 'rejected')
    .order('signed_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Array<SignatureStep & { circuit: CircuitWithSteps | null }>;
}

export async function listMySentCircuits(): Promise<CircuitWithSteps[]> {
  const collab = useAuthStore.getState().collaborator;
  if (!collab) return [];
  const { data, error } = await supabase
    .from('signature_circuits')
    .select(CIRCUIT_SELECT_RELATIONS)
    .eq('initiator_id', collab.id)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data as unknown as CircuitWithSteps[]) ?? [];
}

// ============================================================
// Signature de l'utilisateur (upload + récupération)
// ============================================================

export async function uploadMySignature(file: File): Promise<string> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error('Not authenticated');
  const authUid = session.user.id;
  const path = `${authUid}.png`;

  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Signature trop volumineuse (max 5 Mo)');
  }
  if (file.type !== 'image/png') {
    throw new Error('Format PNG uniquement');
  }

  const { error: uploadErr } = await supabase.storage
    .from('collaborator-signatures')
    .upload(path, file, { upsert: true, contentType: 'image/png' });
  if (uploadErr) throw new Error(uploadErr.message);

  const { error: rpcErr } = await supabase.rpc('update_my_signature', { p_path: path });
  if (rpcErr) throw new Error(rpcErr.message);

  return path;
}

export async function uploadMyParaphe(file: File): Promise<string> {
  const session = (await supabase.auth.getSession()).data.session;
  if (!session) throw new Error('Not authenticated');
  const authUid = session.user.id;
  const path = `${authUid}-paraphe.png`;

  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Initiales trop volumineuses (max 5 Mo)');
  }
  if (file.type !== 'image/png') {
    throw new Error('Format PNG uniquement');
  }

  const { error: uploadErr } = await supabase.storage
    .from('collaborator-signatures')
    .upload(path, file, { upsert: true, contentType: 'image/png' });
  if (uploadErr) throw new Error(uploadErr.message);

  const { error: rpcErr } = await supabase.rpc('update_my_paraphe', { p_path: path });
  if (rpcErr) throw new Error(rpcErr.message);

  return path;
}

function collabStampPath(
  collab: { signature_image_path?: string | null; paraphe_image_path?: string | null } | null,
  key: 'signature_image_path' | 'paraphe_image_path',
): string | null {
  const v = collab?.[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export async function getMySignatureUrl(): Promise<string | null> {
  const collab = useAuthStore.getState().collaborator;
  const path = collabStampPath(collab, 'signature_image_path');
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from('collaborator-signatures')
    .createSignedUrl(path, 600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export async function getMyParapheUrl(): Promise<string | null> {
  const collab = useAuthStore.getState().collaborator as
    | { paraphe_image_path?: string | null }
    | null;
  const path = collabStampPath(collab, 'paraphe_image_path');
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from('collaborator-signatures')
    .createSignedUrl(path, 600);
  if (error) return null;
  return data?.signedUrl ?? null;
}
